import { Button } from "@/components/ui/button";
import { ConfirmacaoPacoteGiro } from "@/components/ConfirmacaoPacoteGiro";
import { SelecaoCompraGiro } from "@/components/CompraRapidaGiro";
import { PacotePromocionalDto, SorteioGiroDto } from "@/dtos/BuscarSorteioVigenteDto";
import { useAuth } from "@/contexts/AuthProvider";
import { labelPlural } from "@/utils/copyTituloBilhete";
import {
    aplicarSelecaoPorQuantidade,
    calcularNudgePacoteGiro,
    criarSelecaoPacote,
    criarSelecaoQuantidade,
} from "@/utils/selecaoPacoteGiro";
import { setSelecaoGiroCheckout } from "@/utils/selecaoGiroCheckout";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ComprarTitulosGiroProps {
    sorteioGiro: SorteioGiroDto;
}

export function ComprarTitulosGiro({ sorteioGiro }: ComprarTitulosGiroProps) {
    const navigate = useNavigate();
    const { isAuthorized } = useAuth();
    const qtdMinima = sorteioGiro.QtdMinima || 1;
    const pacotes = sorteioGiro.PacotesPromocionais ?? [];
    const valorUnitario = sorteioGiro.Valor || 0;
    const vendaBloqueada = sorteioGiro.VendaBloqueada;

    const [quantidade, setQuantidade] = useState(qtdMinima);
    const [quantidadeAvulsaAnterior, setQuantidadeAvulsaAnterior] = useState(qtdMinima);
    const [pacoteSelecionado, setPacoteSelecionado] = useState<PacotePromocionalDto | null>(null);
    const [selecao, setSelecao] = useState<SelecaoCompraGiro>(() =>
        criarSelecaoQuantidade(qtdMinima, valorUnitario)
    );

    const nudge =
        selecao.modo === "quantidade"
            ? calcularNudgePacoteGiro(quantidade, valorUnitario, pacotes)
            : null;

    function onSelecaoChange(
        novaSelecao: SelecaoCompraGiro,
        novaQtd: number,
        pacote: PacotePromocionalDto | null
    ) {
        setSelecao(novaSelecao);
        setQuantidade(novaQtd);
        setPacoteSelecionado(pacote);
    }

    function lembrarQuantidadeAvulsa() {
        if (!pacoteSelecionado) {
            setQuantidadeAvulsaAnterior(quantidade);
        }
    }

    function ajustarQuantidade(novaQtd: number) {
        lembrarQuantidadeAvulsa();
        const resultado = aplicarSelecaoPorQuantidade(
            novaQtd,
            qtdMinima,
            valorUnitario,
            pacotes
        );
        onSelecaoChange(resultado.selecao, resultado.quantidade, resultado.pacote);
    }

    function handleSelecionarPacote(pacote: PacotePromocionalDto) {
        lembrarQuantidadeAvulsa();
        onSelecaoChange(criarSelecaoPacote(pacote), pacote.Qtd, pacote);
    }

    function handleNudgeClick() {
        if (!nudge) {
            return;
        }
        handleSelecionarPacote(nudge.pacote);
    }

    function handleAlterarSelecao() {
        const qtd = Math.max(qtdMinima, quantidadeAvulsaAnterior);
        onSelecaoChange(criarSelecaoQuantidade(qtd, valorUnitario), qtd, null);
    }

    function handleFinalizarCompra() {
        if (selecao.quantidade === 0 || vendaBloqueada) {
            return;
        }

        setSelecaoGiroCheckout(selecao);

        if (isAuthorized) {
            navigate("/compra/finalizar/giro");
        } else {
            navigate("/Acessar?redirect=compra/finalizar/giro");
        }
    }

    return (
        <div className="flex flex-col items-center justify-center gap-4 py-4">
            <h1 className="text-center text-primary font-semibold text-xl">
                Sorteio Giro — {sorteioGiro.DataFormatada}
            </h1>

            {pacoteSelecionado ? (
                <ConfirmacaoPacoteGiro
                    pacote={pacoteSelecionado}
                    ctaLabel="Finalizar Compra"
                    disabled={vendaBloqueada || selecao.quantidade === 0}
                    ctaClassName="mt-2 w-56"
                    onContinuar={handleFinalizarCompra}
                    onAlterarSelecao={handleAlterarSelecao}
                />
            ) : (
                <>
                    <h2 className="text-center text-primary font-semibold text-lg">
                        Quantidade de {labelPlural()}
                    </h2>

                    {pacotes.length > 0 && (
                        <div className="flex flex-wrap items-center justify-center gap-2 px-2">
                            {pacotes.map((pacote) => (
                                <Button
                                    key={pacote.IdPacotePromocional}
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleSelecionarPacote(pacote)}
                                >
                                    {pacote.Qtd} {labelPlural()}
                                    {pacote.Valor > 0 && (
                                        <span className="ml-1 text-xs opacity-90">
                                            {pacote.Valor.toLocaleString("pt-br", {
                                                style: "currency",
                                                currency: "BRL",
                                            })}
                                        </span>
                                    )}
                                </Button>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center justify-center gap-3">
                        <Button
                            type="button"
                            disabled={quantidade <= qtdMinima}
                            onClick={() => ajustarQuantidade(quantidade - 1)}
                            size="lg"
                            variant="outline"
                        >
                            <Minus size={20} />
                        </Button>
                        <span className="flex flex-col select-none justify-center items-center text-primary text-sm font-semibold">
                            <span>{quantidade}</span>
                            <span>{labelPlural()}</span>
                        </span>
                        <Button
                            type="button"
                            onClick={() => ajustarQuantidade(quantidade + 1)}
                            size="lg"
                            variant="outline"
                        >
                            <Plus size={20} />
                        </Button>
                    </div>

                    {nudge && (
                        <button
                            type="button"
                            onClick={handleNudgeClick}
                            className="text-sm text-primary underline hover:no-underline text-center px-2"
                        >
                            {nudge.mensagem}
                        </button>
                    )}

                    {selecao.valor > 0 && (
                        <p className="text-sm text-muted-foreground">
                            Total:{" "}
                            {selecao.valor.toLocaleString("pt-br", {
                                style: "currency",
                                currency: "BRL",
                            })}
                        </p>
                    )}

                    <Button
                        disabled={selecao.quantidade === 0 || vendaBloqueada}
                        type="button"
                        onClick={handleFinalizarCompra}
                        variant="foreground"
                        className="mt-2 w-56"
                    >
                        {vendaBloqueada ? "Venda indisponível" : "Finalizar Compra"}
                    </Button>
                </>
            )}
        </div>
    );
}
